import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Card, Form, Modal, Nav, Table } from 'react-bootstrap';
import Swal from 'sweetalert2';
import Header from '../layouts/Header';
import axiosInstance from '../services/axiosInterceptor';
import { useLoading } from '../contexts/LoadingContext';

const API = process.env.REACT_APP_API;

const TYPE_LABEL = {
  reader: { text: 'กล่องอ่านบัตร', icon: 'ri-contactless-payment-line', variant: 'primary' },
  display: { text: 'จอแสดงผล', icon: 'ri-tv-2-line', variant: 'info' }
};

// กล่องเคาะทุก 30 วิ ส่วนจอเขียนตอนเปิดหน้า เกณฑ์ "ยังเปิดอยู่" จึงต่างกันมาก
// จอที่เปิดค้างทั้งวันจะไม่เขียนซ้ำ ถ้าใช้เกณฑ์เดียวกับกล่องจะขึ้นว่าออฟไลน์ทั้งที่ทำงานปกติ
const ONLINE_MS = { reader: 90 * 1000, display: 24 * 60 * 60 * 1000 };

const thaiDateTime = (v) => (v
  ? new Date(v).toLocaleString('th-TH', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
  : '—');

const sinceText = (v) => {
  if (!v) return 'ยังไม่เคยเห็น';
  const secs = Math.round((Date.now() - new Date(v).getTime()) / 1000);
  if (secs < 90) return 'เมื่อครู่';
  if (secs < 3600) return `${Math.round(secs / 60)} นาทีที่แล้ว`;
  if (secs < 86400) return `${Math.round(secs / 3600)} ชม.ที่แล้ว`;
  return thaiDateTime(v);
};

const isOnline = (device) => {
  if (!device.last_seen_at) return false;
  return Date.now() - new Date(device.last_seen_at).getTime() < (ONLINE_MS[device.device_type] || ONLINE_MS.reader);
};

const displayUrl = (code) => `${window.location.origin}/guard/gate-scan?display=${encodeURIComponent(code)}`;

// คัดลอกไม่สำเร็จต้องไม่เงียบ — แอดมินจะกดแล้วไปวางในโปรแกรมแฟลชโดยไม่รู้ว่าคลิปบอร์ดว่าง
const copy = async (text, what) => {
  try {
    await navigator.clipboard.writeText(text);
    Swal.fire({ icon: 'success', title: `คัดลอก${what}แล้ว`, timer: 1200, showConfirmButton: false });
  } catch {
    Swal.fire({ icon: 'warning', title: 'คัดลอกอัตโนมัติไม่ได้', text: 'กรุณาเลือกข้อความแล้วคัดลอกเอง' });
  }
};

const emptyGate = { gate_id: null, gate_code: '', gate_name: '', gate_note: '', is_active: true };
const emptyDevice = {
  device_id: null, device_code: '', device_type: 'reader', device_name: '',
  gate_id: '', note: '', is_active: true
};

export default function AdminGateDevices() {
  const { setLoading } = useLoading();
  const [tab, setTab] = useState('devices');
  const [gates, setGates] = useState([]);
  const [devices, setDevices] = useState([]);
  const [network, setNetwork] = useState(null);   // ด่าน IP + คีย์รวม จาก server/.env

  const [gateForm, setGateForm] = useState(null);       // null = ปิดฟอร์ม
  const [deviceForm, setDeviceForm] = useState(null);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      axiosInstance.get(`${API}/admin/gates`),
      axiosInstance.get(`${API}/admin/gate-devices`),
      axiosInstance.get(`${API}/admin/gate-network`)
    ])
      .then(([g, d, n]) => { setGates(g.data || []); setDevices(d.data || []); setNetwork(n.data || null); })
      .catch(() => Swal.fire({ icon: 'error', title: 'โหลดข้อมูลไม่สำเร็จ' }))
      .finally(() => setLoading(false));
  }, [setLoading]);

  useEffect(() => { load(); }, [load]);

  const apiError = (error, fallback) =>
    setFormError(error?.response?.data?.message || fallback);

  /* ── ประตู ──────────────────────────────────────────────────────────── */

  const saveGate = () => {
    setFormError(''); setSaving(true);
    const body = {
      gate_code: gateForm.gate_code,
      gate_name: gateForm.gate_name,
      gate_note: gateForm.gate_note,
      is_active: gateForm.is_active
    };
    const req = gateForm.gate_id
      ? axiosInstance.put(`${API}/admin/gates/${gateForm.gate_id}`, body)
      : axiosInstance.post(`${API}/admin/gates`, body);

    req.then(() => { setGateForm(null); load(); })
      .catch(err => apiError(err, 'บันทึกประตูไม่สำเร็จ'))
      .finally(() => setSaving(false));
  };

  const removeGate = (gate) => {
    Swal.fire({
      icon: 'warning', title: `ลบประตู "${gate.gate_name}"?`,
      text: 'ประวัติการแตะบัตรที่ประตูนี้ยังอยู่ครบ — ลบเฉพาะทะเบียนประตู',
      showCancelButton: true, confirmButtonText: 'ลบ', cancelButtonText: 'ยกเลิก',
      confirmButtonColor: '#dc3545'
    }).then(res => {
      if (!res.isConfirmed) return;
      axiosInstance.delete(`${API}/admin/gates/${gate.gate_id}`)
        .then(load)
        .catch(err => Swal.fire({
          icon: 'error', title: 'ลบไม่ได้',
          text: err?.response?.data?.message || 'ลบประตูไม่สำเร็จ'
        }));
    });
  };

  /* ── อุปกรณ์ ────────────────────────────────────────────────────────── */

  const saveDevice = () => {
    setFormError(''); setSaving(true);
    const body = {
      device_code: deviceForm.device_code,
      device_type: deviceForm.device_type,
      device_name: deviceForm.device_name,
      gate_id: deviceForm.gate_id || null,
      note: deviceForm.note,
      is_active: deviceForm.is_active
    };
    const req = deviceForm.device_id
      ? axiosInstance.put(`${API}/admin/gate-devices/${deviceForm.device_id}`, body)
      : axiosInstance.post(`${API}/admin/gate-devices`, body);

    req.then(() => { setDeviceForm(null); load(); })
      .catch(err => apiError(err, 'บันทึกอุปกรณ์ไม่สำเร็จ'))
      .finally(() => setSaving(false));
  };

  const removeDevice = (device) => {
    Swal.fire({
      icon: 'warning', title: `ลบ "${device.device_name}"?`,
      text: device.device_type === 'reader'
        ? 'กล่องใบนี้จะยิงเข้าระบบไม่ได้อีก จนกว่าจะลงทะเบียนใหม่'
        : 'จอนี้จะเปิดหน้าแสดงผลด้วยลิงก์เดิมไม่ได้อีก',
      showCancelButton: true, confirmButtonText: 'ลบ', cancelButtonText: 'ยกเลิก',
      confirmButtonColor: '#dc3545'
    }).then(res => {
      if (!res.isConfirmed) return;
      axiosInstance.delete(`${API}/admin/gate-devices/${device.device_id}`)
        .then(load)
        .catch(() => Swal.fire({ icon: 'error', title: 'ลบอุปกรณ์ไม่สำเร็จ' }));
    });
  };

  // token โผล่ครั้งเดียว — ปิดกล่องนี้แล้วอ่านซ้ำไม่ได้ ต้องออกใบใหม่
  const showToken = (device, token) => {
    const config = [
      `#define DEVICE_ID  "${device.device_code}"`,
      `#define GATE_KEY   "${token}"`
    ].join('\n');

    Swal.fire({
      icon: 'success',
      title: `token ของ ${device.device_name}`,
      html: `
        <p class="mb-2 text-start">คัดลอกสองบรรทัดนี้ไปวางใน <code>firmware/gate_reader/config.h</code> แล้วแฟลชกล่องใหม่</p>
        <pre class="text-start p-2 rounded" style="background:#f1f3f5;white-space:pre-wrap;word-break:break-all">${config.replace(/</g, '&lt;')}</pre>
        <p class="mb-0 text-start text-danger fw-semibold">ค่านี้แสดงครั้งเดียว ปิดหน้าต่างแล้วดูซ้ำไม่ได้ ถ้าหายต้องออกใหม่</p>
      `,
      showCancelButton: true,
      confirmButtonText: 'คัดลอกทั้งสองบรรทัด',
      cancelButtonText: 'ปิด',
      width: 620
    }).then(res => { if (res.isConfirmed) copy(config, 'ค่าสำหรับ config.h'); });
  };

  const issueToken = (device) => {
    Swal.fire({
      icon: 'warning',
      title: device.token_set_at ? 'ออก token ใหม่?' : `ออก token ให้ ${device.device_name}?`,
      text: device.token_set_at
        ? 'ใบเดิมจะใช้ไม่ได้ทันที กล่องจะยิงเข้าระบบไม่ได้จนกว่าจะแฟลชค่าใหม่ลงไป'
        : 'หลังออก token กล่องใบนี้จะเลิกใช้คีย์รวมใน .env ทันทีที่แฟลชค่าใหม่',
      showCancelButton: true, confirmButtonText: 'ออก token', cancelButtonText: 'ยกเลิก'
    }).then(res => {
      if (!res.isConfirmed) return;
      axiosInstance.post(`${API}/admin/gate-devices/${device.device_id}/token`)
        .then(r => { showToken(device, r.data.token); load(); })
        .catch(err => Swal.fire({
          icon: 'error', title: 'ออก token ไม่สำเร็จ',
          text: err?.response?.data?.message || ''
        }));
    });
  };

  /* ── หน้าจอ ─────────────────────────────────────────────────────────── */

  const gateName = (device) => {
    if (device.gate_name) return device.gate_name;
    return device.device_type === 'display'
      ? <span className="text-secondary">ทุกประตู</span>
      : <span className="text-danger">ยังไม่ผูกประตู</span>;
  };

  return (
    <React.Fragment>
      <Header />
      <div className="main main-app p-3 p-lg-4">

        <div className="d-flex align-items-center justify-content-between mb-4">
          <div>
            <h4 className="main-title mb-1">ประตูและอุปกรณ์ที่ประตู</h4>
            <p className="text-secondary mb-0 fs-sm">
              ลงทะเบียนกล่องอ่านบัตรกับจอที่ป้อมยาม — กล่องได้ token ของตัวเองไปใส่ใน firmware
              ส่วนจอได้ลิงก์ที่ผูกกับประตูไว้แล้ว
            </p>
          </div>
          <Button size="sm" variant="primary"
            onClick={() => {
              setFormError('');
              if (tab === 'gates') setGateForm({ ...emptyGate });
              else setDeviceForm({ ...emptyDevice, gate_id: gates[0]?.gate_id || '' });
            }}>
            <i className="ri-add-line me-1" />{tab === 'gates' ? 'เพิ่มประตู' : 'เพิ่มอุปกรณ์'}
          </Button>
        </div>

        <Nav variant="tabs" activeKey={tab} onSelect={k => setTab(k)} className="mb-3">
          <Nav.Item><Nav.Link eventKey="devices">อุปกรณ์ ({devices.length})</Nav.Link></Nav.Item>
          <Nav.Item><Nav.Link eventKey="gates">ประตู ({gates.length})</Nav.Link></Nav.Item>
        </Nav>

        {tab === 'devices' && network && (
          <Card className="card-one mb-3">
            <Card.Body className="py-3">
              <div className="d-flex flex-wrap align-items-start gap-3">
                <div className="flex-grow-1">
                  <div className="fw-semibold mb-1">
                    <i className="ri-shield-check-line me-1" />ด่านชั้นเครือข่ายของกล่อง
                  </div>
                  <div className="fs-sm text-secondary">
                    กล่องต้องยิงมาจากที่อยู่เหล่านี้เท่านั้น ถึงจะได้ตรวจ token ต่อ —
                    ตั้งที่ <code>GATE_DEVICE_ALLOWED_IPS</code> ใน <code>server/.env</code>{' '}
                    <b>แก้แล้วต้องรีสตาร์ทเซิร์ฟเวอร์</b> เปลี่ยนจากหน้านี้ไม่ได้โดยตั้งใจ
                    เพราะเป็นด่านที่ต้องกันคนที่ยังไม่ผ่านอะไรเลย
                  </div>
                  {/* กล่องโดน 403 ทั้งที่ต่อ WiFi ติด = ที่อยู่เปลี่ยน ไม่ใช่ token ผิด
                      เทียบ "จาก x.x.x.x" ในตารางข้างล่างกับรายการนี้ได้เลย ไม่ต้องไปงม nginx log */}
                  <div className="fs-sm text-secondary mt-1">
                    กล่องที่โดน 403 ทั้งที่ต่อ WiFi ติด ให้เทียบที่อยู่ในคอลัมน์สถานะกับรายการนี้ก่อน
                  </div>
                </div>
                <div className="text-end">
                  {network.usingDefault ? (
                    <Badge bg="warning" text="dark">ไม่ได้ตั้ง — ใช้ค่าเดิม (loopback + วงส่วนตัว)</Badge>
                  ) : (
                    network.allowedIps.map(ip => (
                      <Badge bg="light" text="dark" className="border ms-1" key={ip}>{ip}</Badge>
                    ))
                  )}
                  {!network.sharedKeyConfigured && (
                    <div className="fs-sm text-secondary mt-2">
                      ไม่ได้ตั้ง <code>GATE_DEVICE_KEY</code> — กล่องที่ยังไม่มี token ของตัวเองจะใช้งานไม่ได้
                    </div>
                  )}
                </div>
              </div>
            </Card.Body>
          </Card>
        )}

        {tab === 'devices' && (
          <Card className="card-one">
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table hover className="mb-0 align-middle">
                  <thead className="table-secondary">
                    <tr>
                      <th>อุปกรณ์</th>
                      <th style={{ width: 150 }}>ประตู</th>
                      <th style={{ width: 190 }}>สถานะ</th>
                      <th style={{ width: 230 }}>token / ลิงก์</th>
                      <th style={{ width: 190 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {devices.map(d => {
                      const type = TYPE_LABEL[d.device_type] || TYPE_LABEL.reader;
                      return (
                        <tr key={d.device_id} className={d.is_active ? '' : 'opacity-50'}>
                          <td>
                            <div className="fw-semibold">
                              <i className={`${type.icon} me-1`} />{d.device_name}
                            </div>
                            <div className="fs-sm text-secondary">
                              <Badge bg={type.variant} className="me-1">{type.text}</Badge>
                              <code>{d.device_code}</code>
                            </div>
                          </td>
                          <td>{gateName(d)}</td>
                          <td>
                            {!d.is_active
                              ? <Badge bg="secondary">ปิดใช้งาน</Badge>
                              : <Badge bg={isOnline(d) ? 'success' : 'danger'}>
                                  {isOnline(d) ? 'ออนไลน์' : 'ไม่พบสัญญาณ'}
                                </Badge>}
                            <div className="fs-sm text-secondary mt-1">{sinceText(d.last_seen_at)}</div>
                            {d.last_seen_ip && (
                              <div className="fs-sm text-secondary">จาก <code>{d.last_seen_ip}</code></div>
                            )}
                          </td>
                          <td className="fs-sm">
                            {d.device_type === 'reader' ? (
                              d.token_set_at ? (
                                <>
                                  <div>ใช้ token ของตัวเอง <code>…{d.token_hint}</code></div>
                                  <div className="text-secondary">ออกเมื่อ {thaiDateTime(d.token_set_at)}</div>
                                </>
                              ) : (
                                <span className="text-warning fw-semibold">
                                  <i className="ri-error-warning-line me-1" />ยังใช้คีย์รวมใน .env
                                </span>
                              )
                            ) : (
                              <Button size="sm" variant="outline-secondary"
                                onClick={() => copy(displayUrl(d.device_code), 'ลิงก์ของจอ')}>
                                <i className="ri-link me-1" />คัดลอกลิงก์เปิดจอ
                              </Button>
                            )}
                          </td>
                          <td className="text-end">
                            {d.device_type === 'reader' && (
                              <Button size="sm" variant="outline-primary" className="me-1"
                                onClick={() => issueToken(d)}>
                                <i className="ri-key-2-line me-1" />
                                {d.token_set_at ? 'ออกใหม่' : 'ออก token'}
                              </Button>
                            )}
                            <Button size="sm" variant="outline-secondary" className="me-1"
                              onClick={() => {
                                setFormError('');
                                setDeviceForm({
                                  device_id: d.device_id, device_code: d.device_code,
                                  device_type: d.device_type, device_name: d.device_name,
                                  gate_id: d.tb_gate_gate_id || '', note: d.note || '',
                                  is_active: !!d.is_active
                                });
                              }}>
                              <i className="ri-pencil-line" />
                            </Button>
                            <Button size="sm" variant="outline-danger" onClick={() => removeDevice(d)}>
                              <i className="ri-delete-bin-line" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                    {!devices.length && (
                      <tr><td colSpan={5} className="text-center text-secondary py-4">ยังไม่มีอุปกรณ์</td></tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        )}

        {tab === 'gates' && (
          <Card className="card-one">
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table hover className="mb-0 align-middle">
                  <thead className="table-secondary">
                    <tr>
                      <th>ประตู</th>
                      <th style={{ width: 160 }}>อุปกรณ์</th>
                      <th style={{ width: 120 }}>สถานะ</th>
                      <th style={{ width: 120 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {gates.map(g => (
                      <tr key={g.gate_id} className={g.is_active ? '' : 'opacity-50'}>
                        <td>
                          <div className="fw-semibold">{g.gate_name}</div>
                          <div className="fs-sm text-secondary">
                            <code>{g.gate_code}</code>{g.gate_note ? ` · ${g.gate_note}` : ''}
                          </div>
                        </td>
                        <td className="fs-sm">
                          กล่อง {Number(g.reader_count) || 0} · จอ {Number(g.display_count) || 0}
                        </td>
                        <td>
                          <Badge bg={g.is_active ? 'success' : 'secondary'}>
                            {g.is_active ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
                          </Badge>
                        </td>
                        <td className="text-end">
                          <Button size="sm" variant="outline-secondary" className="me-1"
                            onClick={() => {
                              setFormError('');
                              setGateForm({
                                gate_id: g.gate_id, gate_code: g.gate_code, gate_name: g.gate_name,
                                gate_note: g.gate_note || '', is_active: !!g.is_active
                              });
                            }}>
                            <i className="ri-pencil-line" />
                          </Button>
                          <Button size="sm" variant="outline-danger" onClick={() => removeGate(g)}>
                            <i className="ri-delete-bin-line" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {!gates.length && (
                      <tr><td colSpan={4} className="text-center text-secondary py-4">ยังไม่มีประตู</td></tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        )}

        {/* ── ฟอร์มประตู ── */}
        <Modal show={!!gateForm} onHide={() => setGateForm(null)} centered>
          <Modal.Header closeButton>
            <Modal.Title as="h6">{gateForm?.gate_id ? 'แก้ไขประตู' : 'เพิ่มประตู'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {formError && <div className="alert alert-danger py-2 fs-sm">{formError}</div>}
            <Form.Group className="mb-3">
              <Form.Label>รหัสประตู</Form.Label>
              <Form.Control
                value={gateForm?.gate_code || ''}
                disabled={!!gateForm?.gate_id}
                placeholder="gate-1"
                onChange={e => setGateForm(f => ({ ...f, gate_code: e.target.value }))}
              />
              <Form.Text>
                a-z 0-9 - _ เท่านั้น ใช้เป็นห้องส่งข้อมูลของจอ — ตั้งแล้วเปลี่ยนไม่ได้
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>ชื่อประตู</Form.Label>
              <Form.Control
                value={gateForm?.gate_name || ''} placeholder="ประตูหน้า"
                onChange={e => setGateForm(f => ({ ...f, gate_name: e.target.value }))}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>หมายเหตุ</Form.Label>
              <Form.Control
                value={gateForm?.gate_note || ''}
                onChange={e => setGateForm(f => ({ ...f, gate_note: e.target.value }))}
              />
            </Form.Group>
            <Form.Check
              type="switch" label="เปิดใช้งาน" checked={!!gateForm?.is_active}
              onChange={e => setGateForm(f => ({ ...f, is_active: e.target.checked }))}
            />
            {gateForm?.gate_id && !gateForm?.is_active && (
              <div className="fs-sm text-danger mt-2">
                ปิดประตูแล้ว กล่องที่ประตูนี้จะยิงเข้าระบบไม่ได้ทันที
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setGateForm(null)}>ยกเลิก</Button>
            <Button variant="primary" disabled={saving} onClick={saveGate}>บันทึก</Button>
          </Modal.Footer>
        </Modal>

        {/* ── ฟอร์มอุปกรณ์ ── */}
        <Modal show={!!deviceForm} onHide={() => setDeviceForm(null)} centered>
          <Modal.Header closeButton>
            <Modal.Title as="h6">{deviceForm?.device_id ? 'แก้ไขอุปกรณ์' : 'เพิ่มอุปกรณ์'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {formError && <div className="alert alert-danger py-2 fs-sm">{formError}</div>}
            <Form.Group className="mb-3">
              <Form.Label>ชนิด</Form.Label>
              <Form.Select
                value={deviceForm?.device_type || 'reader'}
                disabled={!!deviceForm?.device_id}
                onChange={e => setDeviceForm(f => ({ ...f, device_type: e.target.value }))}
              >
                <option value="reader">กล่องอ่านบัตร (ESP32)</option>
                <option value="display">จอแสดงผล</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>รหัสอุปกรณ์</Form.Label>
              <Form.Control
                value={deviceForm?.device_code || ''}
                disabled={!!deviceForm?.device_id}
                placeholder={deviceForm?.device_type === 'display' ? 'tv-gate-1' : 'gate-1'}
                onChange={e => setDeviceForm(f => ({ ...f, device_code: e.target.value }))}
              />
              <Form.Text>
                {deviceForm?.device_type === 'display'
                  ? 'อยู่ในลิงก์ที่เปิดบนจอ — ตั้งแล้วเปลี่ยนไม่ได้'
                  : 'ต้องตรงกับ DEVICE_ID ใน config.h ของกล่อง — ตั้งแล้วเปลี่ยนไม่ได้'}
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>ชื่อที่แสดง</Form.Label>
              <Form.Control
                value={deviceForm?.device_name || ''}
                placeholder={deviceForm?.device_type === 'display' ? 'จอที่ป้อมยามประตูหน้า' : 'กล่องอ่านบัตรประตูหน้า'}
                onChange={e => setDeviceForm(f => ({ ...f, device_name: e.target.value }))}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>ประตู</Form.Label>
              <Form.Select
                value={deviceForm?.gate_id || ''}
                onChange={e => setDeviceForm(f => ({ ...f, gate_id: e.target.value }))}
              >
                <option value="">
                  {deviceForm?.device_type === 'display' ? 'ทุกประตู (จอรวม)' : '— เลือกประตู —'}
                </option>
                {gates.map(g => (
                  <option key={g.gate_id} value={g.gate_id}>{g.gate_name} ({g.gate_code})</option>
                ))}
              </Form.Select>
              <Form.Text>
                {deviceForm?.device_type === 'display'
                  ? 'จอจะขึ้นเฉพาะคนที่แตะบัตรที่ประตูนี้ — ปล่อยว่างไว้ถ้าอยากเห็นทุกประตู'
                  : 'ผลการแตะบัตรของกล่องใบนี้จะส่งไปที่จอของประตูที่เลือก'}
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>หมายเหตุ</Form.Label>
              <Form.Control
                value={deviceForm?.note || ''}
                onChange={e => setDeviceForm(f => ({ ...f, note: e.target.value }))}
              />
            </Form.Group>
            <Form.Check
              type="switch" label="เปิดใช้งาน" checked={!!deviceForm?.is_active}
              onChange={e => setDeviceForm(f => ({ ...f, is_active: e.target.checked }))}
            />
            {deviceForm?.device_id && !deviceForm?.is_active && (
              <div className="fs-sm text-danger mt-2">
                ปิดแล้วอุปกรณ์นี้จะใช้งานไม่ได้ทันที — ใช้ตอนกล่องหายโดยไม่ต้องเปลี่ยนคีย์ของใบอื่น
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setDeviceForm(null)}>ยกเลิก</Button>
            <Button variant="primary" disabled={saving} onClick={saveDevice}>บันทึก</Button>
          </Modal.Footer>
        </Modal>

      </div>
    </React.Fragment>
  );
}
